import { Add, Delete } from '@mui/icons-material';
import {
  MenuItem,
  useMediaQuery,
  useTheme,
  ListItemIcon,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  createDNT,
  getPropertyDNTList,
  getPropertyGroupDNTList,
  getTenantDNTList,
  updateDNT,
} from 'api/doNotTow';
import MobilePanelList from 'components/lists/MobilePanelList';
import { TAB_LAYOUT_HEADER_HEIGHT } from 'components/layout/FormTabNavigation';
import { AppContext } from 'context/appContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import dayjs from 'dayjs';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionFunction,
  useFetcher,
  useLoaderData,
  useOutletContext,
} from 'react-router';
import {
  DisplayMode,
  DoNotTow,
  FormMode,
  PanelRootContext,
  Property,
  PropertyGroup,
  Status,
  TraxeroActionResponse,
} from 'types';
import { capitalize, formatLicensePlateString, getBlankDoNotTow } from 'utils';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import {
  useDefaultTableOptions,
  TableType,
} from 'hooks/useDefaultTableOptions';
import MRTableToolbar from 'components/tables/PanelTableToolbar';
import { ListItemMenuButton } from 'components/buttons/ListItemMenuButton';
import FormFrame from 'components/layout/FormFrame';
import { DNTDialog } from './DNTDialog';
export interface DNTPanelRouteParams {
  params: {
    propertyID?: string;
    tenantID?: string;
    groupID?: string;
    dntID?: string;
  };
}

export interface DNTLoaderData {
  _dntList: Promise<DoNotTow[]>;
}

export const dntLoader = ({ params }: DNTPanelRouteParams): DNTLoaderData => {
  const { propertyID, tenantID, groupID } =
    params as DNTPanelRouteParams['params'];
  if (propertyID) {
    return { _dntList: getPropertyDNTList({ tenantID, propertyID }) };
  }
  if (groupID) {
    return {
      _dntList: getPropertyGroupDNTList({ tenantID, groupID }),
    };
  }
  return { _dntList: getTenantDNTList(tenantID) };
};

export const DNTPanel = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const displayToast = useContext(ToastContext);
  const theme = useTheme();
  const { breakpoints } = theme;
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const fetcher = useFetcher();
  const { _dntList } = useLoaderData() as DNTLoaderData;
  const { formMode, setFormMode, displayMode, entity } =
    useOutletContext<PanelRootContext<PropertyGroup | Property>>();

  const [DNTs, setDNTs] = useState<DoNotTow[]>([]);
  const [dnt, setDNT] = useState<DoNotTow | null>(null);

  // await data fetch
  useEffect(() => {
    const awaitLoaderData = async () => {
      const dnts = await _dntList;
      setDNTs(dnts);
    };
    awaitLoaderData();
  }, [_dntList]);

  // action response handling
  const getSuccessMessage = useCallback(
    (res: TraxeroActionResponse<DNTActionHandler>): string => {
      switch (res.handler) {
        case DNTActionHandler.GROUP_CREATE:
        case DNTActionHandler.PROPERTY_CREATE:
          return t('create_success', {
            name: t('donottow.label', { ns: 'entities' }),
            ns: 'toasts',
          });
        case DNTActionHandler.GROUP_REMOVE:
        case DNTActionHandler.PROPERTY_REMOVE:
          return t('remove_success', {
            name: t('donottow.label', { ns: 'entities' }),
            ns: 'toasts',
          });
        case DNTActionHandler.GROUP_UPDATE:
        case DNTActionHandler.PROPERTY_UPDATE:
          return t('update_success', {
            name: t('donottow.label', { ns: 'entities' }),
            ns: 'toasts',
          });
        default:
          return '';
      }
    },
    [],
  );
  const getErrorMessage = useCallback(
    (res: TraxeroActionResponse<DNTActionHandler>): string => {
      const { handler, status, statusText } = res;
      switch (handler) {
        case DNTActionHandler.GROUP_CREATE:
        case DNTActionHandler.PROPERTY_CREATE:
          if (status === 409) {
            return t('duplicate_dnt_error', { ns: 'toasts' });
          }
          return t('create_error', {
            name: t('donottow.label', { ns: 'entities' }),
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case DNTActionHandler.GROUP_REMOVE:
        case DNTActionHandler.PROPERTY_REMOVE:
          return t('remove_error', {
            name: t('donottow.label', { ns: 'entities' }),
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case DNTActionHandler.GROUP_UPDATE:
        case DNTActionHandler.PROPERTY_UPDATE:
          return t('update_error', {
            name: t('list', { name: t('donottow.label', { ns: 'entities' }) }),
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        default:
          return '';
      }
    },
    [],
  );

  useEffect(() => {
    if (fetcher.data) {
      const response = fetcher.data as TraxeroActionResponse<DNTActionHandler>;
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });

        handleCloseDialog();
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    }
  }, [fetcher.data]);

  const canAddRemove: boolean = useMemo(() => {
    if (displayMode === 'property') {
      return state.entityPermissions.property.update;
    }
    if (displayMode === 'group') {
      return state.entityPermissions.property_group.update;
    }
    return false;
  }, [displayMode]);

  const handleRemoveDNT = useCallback(
    (item: DoNotTow) => {
      const removed: DoNotTow = {
        ...item,
        status: Status.Disabled,
      };
      fetcher.submit(
        {
          handler: getHandler(displayMode),
          body: JSON.stringify(removed),
          dntID: item.id,
          tenantID: state.tenant.id,
        },
        {
          method: 'PUT',
          encType: 'application/json',
        },
      );
    },
    [displayMode, state.tenant.id],
  );
  const handleItemClick = (item: DoNotTow) => {
    setFormMode('edit');
    setDNT(item);
  };
  const handleCreateClick = () => {
    setFormMode('create');
    setDNT(getBlankDoNotTow(state.tenant.id, entity?.id || 0, displayMode));
  };
  const handleCloseDialog = () => {
    setFormMode('none');
    setDNT(null);
  };
  const timeRangesOverlap = (
    startA: string | Date,
    endA: string | Date | undefined,
    startB: string | Date,
    endB: string | Date | undefined,
  ): boolean => {
    const sA = new Date(startA).getTime();
    const eA = endA ? new Date(endA).getTime() : Infinity;
    const sB = new Date(startB).getTime();
    const eB = endB ? new Date(endB).getTime() : Infinity;
    return sA < eB && sB < eA;
  };

  const handleDialogSubmit = () => {
    if (formMode === 'create' && dnt?.plate?.number && dnt?.plate?.state) {
      const duplicate = DNTs.find(
        (existing) =>
          existing.status !== Status.Disabled &&
          existing.entityID === dnt.entityID &&
          existing.plate?.number?.toLowerCase() ===
            dnt.plate?.number?.toLowerCase() &&
          existing.plate?.state === dnt.plate?.state &&
          timeRangesOverlap(
            existing.startTime,
            existing.endTime,
            dnt.startTime,
            dnt.endTime,
          ),
      );
      if (duplicate) {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: t('duplicate_dnt_error', { ns: 'toasts' }),
        });
        return;
      }
    }

    fetcher.submit(
      {
        handler: getHandler(displayMode, formMode),
        body: JSON.stringify(dnt),
        dntID: dnt?.id || 0,
        tenantID: state.tenant.id,
      },
      {
        method: 'POST',
        encType: 'application/json',
      },
    );
  };

  const columns = useMemo<MRT_ColumnDef<DoNotTow>[]>(
    () => [
      {
        header: t('donottow.id', { ns: 'entities' }),
        accessorKey: 'id',
      },
      {
        header: t('donottow.plate.label', { ns: 'entities' }),
        id: 'plateNumber',
        accessorKey: 'plate.number',
        accessorFn: (row) => formatLicensePlateString(row.plate),
      },
      {
        header: t('donottow.reason', { ns: 'entities' }),
        accessorKey: 'reason',
      },
      {
        header: t('donottow.startTime', { ns: 'entities' }),
        accessorKey: 'startTime',
        accessorFn: (row) => new Date(row.startTime),
        filterVariant: 'datetime-range',
        Cell: ({ cell }) => {
          return dayjs(cell.getValue<Date>()).format('MM/DD/YYYY hh:mm a');
        },
      },
      {
        header: t('donottow.endTime', { ns: 'entities' }),
        accessorKey: 'endTime',
        accessorFn: (row) => {
          if (row.endTime) {
            return new Date(row.endTime);
          }
          return undefined;
        },
        filterVariant: 'datetime-range',
        sortUndefined: 'last',
        Cell: ({ cell }) => {
          const value = cell.getValue<Date | undefined>();
          if (!value) {
            return '';
          }
          return dayjs(value).format('MM/DD/YYYY hh:mm a');
        },
      },
      {
        header: t('donottow.status', { ns: 'entities' }),
        accessorKey: 'status',
        filterVariant: 'select',
        filterSelectOptions: [
          {
            label: t('status.active', { ns: 'enums' }),
            value: Status.Active,
          },
          {
            label: t('status.disabled', { ns: 'enums' }),
            value: Status.Disabled,
          },
        ],
        Cell: ({ renderedCellValue }) => {
          return capitalize(renderedCellValue);
        },
      },
      {
        header: t('donottow.ancestry.label', { ns: 'entities' }),
        accessorKey: 'ancestry',
        accessorFn: (row) => row.ancestry?.propertyGroupName,
      },
    ],
    [],
  );
  const tableDefaults = useDefaultTableOptions<DoNotTow>(TableType.NESTED);
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: DNTs,
    columns,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'compact',
      columnVisibility: {
        id: false,
        vin: false,
        reason: true,
        plate: true,
        startTime: true,
        endTime: true,
        status: true,
        ancestry: displayMode === 'group',
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading: fetcher.state !== 'idle',
      showLoadingOverlay: false,
    },
    getRowId: (row) => `${row.id}`,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        cursor: 'pointer',
        width: '100%',
      },
      onClick: () => handleItemClick(row.original),
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        isSubmitting={fetcher.state === 'submitting'}
        optionsDisabled={!canAddRemove}
        tableType={TableType.NESTED}
        menuOptions={[
          {
            onClick: handleCreateClick,
            content: t('buttons.create', {
              name: t('donottow.label', { ns: 'entities' }),
            }),
            disabled: !canAddRemove,
            icon: <Add />,
          },
        ]}
      >
        <Stack
          direction={'row'}
          flexGrow={1}
          justifyContent={'flex-start'}
          alignItems={'center'}
        >
          <Typography variant='h3' textTransform={'capitalize'}>
            {t('donottow.label_plural', { ns: 'entities' })}
          </Typography>
        </Stack>
      </MRTableToolbar>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      <MenuItem
        key={1}
        divider
        onClick={() => {
          handleRemoveDNT(row.original);
          closeMenu();
        }}
        sx={{ m: 0, backgroundColor: 'white' }}
      >
        <ListItemIcon>
          <Delete />
        </ListItemIcon>
        {t('buttons.remove', { name: t('donottow.label', { ns: 'entities' }) })}
      </MenuItem>,
    ],
  });

  if (mobile) {
    // mobile list
    const itemHeight = 70;
    const renderItem = (dnt: DoNotTow): ReactNode => (
      <ListItem
        key={dnt.id}
        id={dnt.id.toString()}
        onClick={() => handleItemClick(dnt)}
        divider
        secondaryAction={
          <ListItemMenuButton
            menuOptions={[
              {
                onClick: () => handleRemoveDNT(dnt),
                content: t('buttons.remove', { name: t('item') }),
                disabled: !canAddRemove,
              },
            ]}
          />
        }
        sx={{ height: itemHeight, backgroundColor: 'white' }}
      >
        <ListItemText
          primary={`${formatLicensePlateString(dnt.plate)}`}
          secondary={`${dnt.startTime} - ${dnt.endTime || ''}`}
          primaryTypographyProps={{
            noWrap: true,
            variant: 'subtitle1',
          }}
          secondaryTypographyProps={{ noWrap: true, variant: 'subtitle2' }}
        />
      </ListItem>
    );

    return (
      <>
        {dnt && (
          <DNTDialog
            dnt={dnt}
            handleCancel={handleCloseDialog}
            handleSubmit={handleDialogSubmit}
            formMode={formMode}
            setDNT={setDNT}
            open={!!dnt}
          />
        )}
        <MobilePanelList<DoNotTow>
          data={DNTs}
          columns={columns.map((c) => ({
            name: c.header,
            search: c.accessorKey,
          }))}
          renderItem={renderItem}
          title={t('donottow.label_plural', { ns: 'entities' })}
          itemHeight={itemHeight}
          withSearchBar
          heightOffset={TAB_LAYOUT_HEADER_HEIGHT}
          menuOptions={[
            {
              onClick: handleCreateClick,
              content: t('buttons.create', {
                name: t('donottow.label', { ns: 'entities' }),
              }),
              disabled: !canAddRemove,
              icon: <Add />,
            },
          ]}
        />
      </>
    );
  }

  return (
    <FormFrame>
      {dnt && (
        <DNTDialog
          dnt={dnt}
          handleCancel={handleCloseDialog}
          handleSubmit={handleDialogSubmit}
          formMode={formMode}
          setDNT={setDNT}
          open={!!dnt}
        />
      )}
      <MaterialReactTable table={table} />
    </FormFrame>
  );
};

export enum DNTActionHandler {
  PROPERTY_CREATE = 'PROPERTY_CREATE',
  GROUP_CREATE = 'GROUP_CREATE',
  TENANT_CREATE = 'TENANT_CREATE',
  PROPERTY_REMOVE = 'PROPERTY_REMOVE',
  GROUP_REMOVE = 'GROUP_REMOVE',
  PROPERTY_UPDATE = 'PROPERTY_UPDATE',
  GROUP_UPDATE = 'GROUP_UPDATE',
  TENANT_UPDATE = 'TENANT_UPDATE',
  UNKNOWN_HANDLER = 'UNKNOWN_HANDLER',
}

export const getHandler = (dm: DisplayMode, fm: FormMode = 'none') => {
  switch (`${dm}-${fm}`) {
    case 'property-create':
      return DNTActionHandler.PROPERTY_CREATE;
    case 'group-create':
      return DNTActionHandler.GROUP_CREATE;
    case 'tenant-create':
      return DNTActionHandler.TENANT_CREATE;
    case 'group-none':
      return DNTActionHandler.GROUP_REMOVE;
    case 'property-none':
      return DNTActionHandler.PROPERTY_REMOVE;
    case 'group-edit':
      return DNTActionHandler.GROUP_UPDATE;
    case 'tenant-edit':
      return DNTActionHandler.TENANT_UPDATE;
    case 'property-edit':
      return DNTActionHandler.PROPERTY_UPDATE;
    default:
      return DNTActionHandler.UNKNOWN_HANDLER;
  }
};

export const dntPanelAction: ActionFunction = async ({
  request,
}): Promise<TraxeroActionResponse<DNTActionHandler>> => {
  const { body, handler, dntID, tenantID } = await request.json();
  let res: Response;
  switch (handler) {
    case DNTActionHandler.TENANT_CREATE:
    case DNTActionHandler.GROUP_CREATE:
    case DNTActionHandler.PROPERTY_CREATE: {
      res = await createDNT({
        tenantID,
        body,
      });
      break;
    }
    default: {
      res = await updateDNT({
        tenantID,
        body,
        dntID,
      });
      break;
    }
  }
  return { handler, status: res.status, statusText: res.statusText };
};
